import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../lib/api-client';
import { programKeys } from './usePrograms';
import type { LessonSession } from '@lms/shared/types';

export const sessionKeys = {
  all: ['lesson-sessions'] as const,
  lists: () => [...sessionKeys.all, 'list'] as const,
  list: (subjectId: string) => [...sessionKeys.all, 'list', subjectId] as const,
  details: () => [...sessionKeys.all, 'detail'] as const,
  detail: (id: string) => [...sessionKeys.details(), id] as const,
};

export function useSessions(subjectId: string) {
  return useQuery({
    queryKey: sessionKeys.list(subjectId),
    queryFn: () => apiClient.lessonSessions.list(subjectId),
    enabled: !!subjectId,
  });
}

export function useCreateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      data: Partial<LessonSession> & {
        subjectId: string;
        sessionNumber: number;
        title: string;
      };
    }) => apiClient.lessonSessions.create(variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

export function useBulkCreateSessions() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: {
      programId: string;
      data: { subjectId: string; count: number; titlePrefix?: string };
    }) => apiClient.lessonSessions.bulkCreate(variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

export function useUpdateSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string; data: Partial<LessonSession> }) =>
      apiClient.lessonSessions.update(variables.id, variables.data),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}

export function useDeleteSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (variables: { id: string; programId: string }) =>
      apiClient.lessonSessions.delete(variables.id),
    onSuccess: (_, variables) => {
      void queryClient.invalidateQueries({
        queryKey: programKeys.detail(variables.programId),
      });
      void queryClient.invalidateQueries({ queryKey: sessionKeys.lists() });
    },
  });
}
